/**
 * Utility functions for handling facility display and formatting
 */

/**
 * Extracts title and subtitle from facility name with brackets
 * Takes the LAST bracket content as subtitle and preserves other brackets in title
 * Removes any dash before the last bracket
 * @param {string} facilityName - Full facility name with potential brackets
 * @returns {object} - Object with title and subtitle properties
 * @example
 * extractFacilityTitleAndSubtitle("Hospital (General)") 
 * // returns { title: "Hospital", subtitle: "General" }
 * extractFacilityTitleAndSubtitle("test (one) - (two)") 
 * // returns { title: "test (one)", subtitle: "two" }
 * extractFacilityTitleAndSubtitle("Hospital (General) - (Downtown)") 
 * // returns { title: "Hospital (General)", subtitle: "Downtown" }
 * extractFacilityTitleAndSubtitle("Place (A) (B) (C)") 
 * // returns { title: "Place (A) (B)", subtitle: "C" }
 * extractFacilityTitleAndSubtitle("Medical Center") 
 * // returns { title: "Medical Center", subtitle: "" }
 */
export const extractFacilityTitleAndSubtitle = (facilityName) => {
  if (!facilityName) return { title: '', subtitle: '' };
  
  // Match all content within parentheses
  const bracketPattern = /\([^)]+\)/g;
  const matches = facilityName.match(bracketPattern);
  
  if (!matches || matches.length === 0) {
    // No brackets found, return the whole name as title
    return { title: facilityName, subtitle: '' };
  }
  
  // Get the last bracket match
  const lastBracket = matches[matches.length - 1];
  
  // Extract the content from the last bracket (remove the parentheses)
  const subtitle = lastBracket.replace(/[()]/g, '').trim();
  
  // Find the index where the last bracket starts
  const lastBracketIndex = facilityName.lastIndexOf(lastBracket);
  
  // Extract everything before the last bracket as the title
  let title = facilityName.substring(0, lastBracketIndex).trim();
  
  // Remove any trailing dash (with optional spaces) before the last bracket
  title = title.replace(/\s*-\s*$/, '').trim();
  
  // Clean up multiple spaces that might result
  title = title.replace(/\s+/g, ' ').trim();
  
  return { title, subtitle };
};